import React, { useMemo, useState } from 'react';
import { useIntl } from 'react-intl';
import type { BankFootageQuery } from 'fym-shared/src/music24/musicVideoApi';
import { TypedMemo } from '../../../common/types';
import { ErrorView } from '../../../common/views/ErrorView/ErrorView';
import { LoadingView } from '../../../common/views/LoadingView';
import { bankFootageView, usageCounts } from '../../footage/footageView';
import { useBankSearch } from '../../footage/useBankSearch';
import { useMusicVideoEditor } from '../../MusicVideoEditorContext';
import { footageMessages } from '../../messages/footageMessages';
import { ShowcaseSearchBusy } from '../../showcase/ShowcaseSearchBusy';
import { ClipCard } from './ClipCard';
import { useAddClip } from './useAddClip';
/* eslint-disable css-modules/no-unused-class -- one stylesheet for the editor; each part uses its own slice of it */
import styles from '../../MusicVideoEditor.module.scss';
/* eslint-enable css-modules/no-unused-class */

/** The clips of one page of results; "Show more" asks for a longer list. */
export const BANK_RESULTS_PAGE = 24;

/**
 * The bank clips that match the search text and the chips (design b1, `GET /footage/bank`; in showcase
 * mode the public search, whose 429 shows the busy line of design c4).
 */
const BankResultsComponent: React.FC<{ query: BankFootageQuery }> = ({ query }) => {
  const intl = useIntl();
  const { doc } = useMusicVideoEditor();
  const { add } = useAddClip();
  const [limit, setLimit] = useState(BANK_RESULTS_PAGE);
  const results = useBankSearch({ ...query, limit });
  const views = useMemo(() => (results.data?.items ?? []).map(bankFootageView), [results.data]);
  const uses = useMemo(() => usageCounts(doc.clips), [doc.clips]);

  if (results.isLoading) {
    return <LoadingView />;
  }
  if (results.isBusy) {
    return <ShowcaseSearchBusy />;
  }
  if (results.error !== undefined && results.data === undefined) {
    return <ErrorView error={results.error} retry={results.retry} />;
  }
  return (
    <>
      <div className={styles.sectionHead}>
        <b>{intl.formatMessage(footageMessages.results)}</b>
      </div>
      {views.length === 0 ? (
        <p className={styles.hint}>{intl.formatMessage(footageMessages.bankEmpty)}</p>
      ) : (
        <div className={styles.clipGrid}>
          {views.map((view) => (
            <ClipCard key={view.key} view={view} uses={uses.get(view.key) ?? 0} onPick={add} />
          ))}
        </div>
      )}
      {results.data?.nextCursor !== null && results.data !== undefined && (
        <button
          type="button"
          className={styles.linkButton}
          onClick={() => setLimit((value) => value + BANK_RESULTS_PAGE)}
        >
          {intl.formatMessage(footageMessages.bankMore)}
        </button>
      )}
    </>
  );
};

export const BankResults = TypedMemo(BankResultsComponent);
